// V1.37.12 · Diálogo de creación rápida de Autorización (DGT o Menor).
// Se persiste como registro en la colección `contracts` con `document_kind`
// = "autorizacion_dgt" | "autorizacion_menor" para reusar TODA la infra
// existente (firma remota, WhatsApp Kai, historial, editar, eliminar).
import React, { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Loader2, Save } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { todayISO } from "@/lib/utils-app";

const KIND_META = {
  autorizacion_dgt: {
    title: "Nueva Autorización DGT",
    description: "Autorización de representación ante la Dirección General de Tráfico. Si el alumno es menor, firmará el tutor legal.",
  },
  autorizacion_menor: {
    title: "Nueva Autorización representante legal (menor)",
    description: "Autorización del tutor legal para alumno menor de edad. La firma del tutor es obligatoria; la del alumno, opcional.",
  },
};

function calcIsMinor(s) {
  if (!s || !s.birth_date) return false;
  const bd = new Date(String(s.birth_date).slice(0, 10) + "T00:00:00");
  if (Number.isNaN(bd.getTime())) return false;
  const t = new Date();
  let age = t.getFullYear() - bd.getFullYear();
  const m = t.getMonth() - bd.getMonth();
  if (m < 0 || (m === 0 && t.getDate() < bd.getDate())) age--;
  return age < 18;
}

export default function AutorizacionDialog({
  open, onClose, studentId, kind, students = [], onCreated, embedded = false,
}) {
  const [selectedStudentId, setSelectedStudentId] = useState(studentId || "");
  const [fecha, setFecha] = useState(todayISO());
  const [tutor, setTutor] = useState({
    nombre: "", dni: "", direccion: "", cp: "", localidad: "", provincia: "",
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open || embedded) {
      setSelectedStudentId(studentId || "");
      setFecha(todayISO());
      setTutor({ nombre: "", dni: "", direccion: "", cp: "", localidad: "", provincia: "" });
    }
  }, [open, embedded, studentId]);

  const student = useMemo(
    () => students.find((s) => s.id === selectedStudentId),
    [students, selectedStudentId],
  );
  const isMinor = useMemo(() => calcIsMinor(student), [student]);

  useEffect(() => {
    if (!student) return;
    setTutor((prev) => ({
      nombre: prev.nombre || student.tutor_nombre || "",
      dni: prev.dni || student.tutor_dni || "",
      direccion: prev.direccion || student.tutor_direccion || student.address || "",
      cp: prev.cp || student.tutor_cp || student.postal_code || "",
      localidad: prev.localidad || student.tutor_localidad || student.city || "",
      provincia: prev.provincia || student.tutor_provincia || student.province || "",
    }));
  }, [selectedStudentId, student]);

  const needsTutor =
    kind === "autorizacion_menor" ||
    (kind === "autorizacion_dgt" && isMinor);
  const tutorValid =
    !needsTutor || (tutor.nombre.trim() && tutor.dni.trim());
  const canSave = selectedStudentId && fecha && tutorValid && !saving;

  const meta = KIND_META[kind] || { title: "Nueva Autorización", description: "" };

  const handleSave = async () => {
    if (!canSave) return;
    setSaving(true);
    try {
      const payload = {
        student_id: selectedStudentId,
        fecha,
        permiso: student?.permiso || "B",
        document_kind: kind,
        tutor_snapshot: needsTutor ? tutor : null,
        // Campos económicos vacíos — no aplican a autorizaciones
        line_items: [],
        precio_lista: 0, descuento_pct: 0, descuento_importe: 0,
        precio_final: 0, total: 0, entrada: 0, saldo: 0,
        matricula: 0, tasa_dgt: 0, precio_clase: 0, precio_pack5: 0,
        cuotas: [],
      };
      const { data } = await api.post("/contracts", payload);
      toast.success(kind === "autorizacion_dgt"
        ? "Autorización DGT creada"
        : "Autorización representante legal creada");
      onCreated && onCreated(data);
      onClose && onClose();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Error al crear");
    } finally {
      setSaving(false);
    }
  };

  const content = (
    <>
        <div className="space-y-3 py-2">
          {!studentId && (
            <div>
              <label className="text-xs font-semibold mb-1 block">Alumno *</label>
              <select
                value={selectedStudentId}
                onChange={(e) => setSelectedStudentId(e.target.value)}
                data-testid="autorizacion-student"
                className="w-full rounded-lg border border-neutral-300 bg-white px-3 h-10 text-sm"
              >
                <option value="">— Selecciona un alumno —</option>
                {students.map((s) => {
                  const fn = [s.name, s.primer_apellido, s.segundo_apellido].filter(Boolean).join(" ");
                  return <option key={s.id} value={s.id}>{fn || s.name}</option>;
                })}
              </select>
            </div>
          )}
          <div>
            <label className="text-xs font-semibold mb-1 block">Fecha *</label>
            <input
              type="date"
              value={fecha}
              onChange={(e) => setFecha(e.target.value)}
              data-testid="autorizacion-fecha"
              className="w-full rounded-lg border border-neutral-300 bg-white px-3 h-10 text-sm"
            />
          </div>

          {needsTutor && (
            <div className="rounded-lg border border-amber-200 bg-amber-50/40 p-3 space-y-2">
              <div className="text-xs font-semibold text-amber-900">
                Datos del tutor legal {kind === "autorizacion_dgt" ? "(alumno menor)" : ""}
              </div>
              <input
                type="text" placeholder="Nombre completo *"
                value={tutor.nombre}
                onChange={(e) => setTutor({ ...tutor, nombre: e.target.value })}
                data-testid="autorizacion-tutor-nombre"
                className="w-full rounded-lg border border-neutral-300 bg-white px-3 h-9 text-sm"
              />
              <input
                type="text" placeholder="DNI/NIE *"
                value={tutor.dni}
                onChange={(e) => setTutor({ ...tutor, dni: e.target.value.toUpperCase() })}
                data-testid="autorizacion-tutor-dni"
                className="w-full rounded-lg border border-neutral-300 bg-white px-3 h-9 text-sm"
              />
              <input
                type="text" placeholder="Domicilio"
                value={tutor.direccion}
                onChange={(e) => setTutor({ ...tutor, direccion: e.target.value })}
                className="w-full rounded-lg border border-neutral-300 bg-white px-3 h-9 text-sm"
              />
              <div className="grid grid-cols-3 gap-2">
                <input type="text" placeholder="CP" value={tutor.cp}
                  onChange={(e) => setTutor({ ...tutor, cp: e.target.value })}
                  className="rounded-lg border border-neutral-300 bg-white px-2 h-9 text-sm" />
                <input type="text" placeholder="Localidad" value={tutor.localidad}
                  onChange={(e) => setTutor({ ...tutor, localidad: e.target.value })}
                  className="rounded-lg border border-neutral-300 bg-white px-2 h-9 text-sm" />
                <input type="text" placeholder="Provincia" value={tutor.provincia}
                  onChange={(e) => setTutor({ ...tutor, provincia: e.target.value })}
                  className="rounded-lg border border-neutral-300 bg-white px-2 h-9 text-sm" />
              </div>
            </div>
          )}

          {kind === "autorizacion_dgt" && student && !isMinor && (
            <div className="text-[11px] text-emerald-800 bg-emerald-50 border border-emerald-200 rounded px-2 py-1.5">
              Alumno mayor de edad: firma el propio alumno.
            </div>
          )}
        </div>

        <DialogFooter className="flex-row items-center justify-between gap-2">
          {!embedded && (
            <Button variant="outline" onClick={() => onClose && onClose()} disabled={saving} className="text-xs">Cancelar</Button>
          )}
          <Button onClick={handleSave} disabled={!canSave} className="text-xs bg-indigo-600 hover:bg-indigo-700" data-testid="autorizacion-save">
            {saving ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Save className="h-3.5 w-3.5 mr-1" />}
            Crear autorización
          </Button>
        </DialogFooter>
    </>
  );

  if (embedded) {
    return (
      <div data-testid={`autorizacion-embedded-${kind}`}>
        <p className="text-xs text-neutral-500 mb-1">{meta.description}</p>
        {content}
      </div>
    );
  }

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose && onClose()}>
      <DialogContent className="max-w-md" data-testid="autorizacion-dialog">
        <DialogHeader>
          <DialogTitle className="text-base">{meta.title}</DialogTitle>
          <p className="text-xs text-neutral-500 mt-0.5">{meta.description}</p>
        </DialogHeader>
        {content}
      </DialogContent>
    </Dialog>
  );
}
